import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import './Knowledge.css';

interface KnowledgeArticle {
  id: number;
  question: string;
  answer: string;
  category: string;
  category_display: string;
  keywords: string;
  answered_by: string;
  times_used: number;
  created_at: string;
  created_by_name: string | null;
}

const categories = [
  { value: '', label: 'All Categories' },
  { value: 'feed', label: 'Feed & Nutrition' },
  { value: 'shipping', label: 'Shipping & Delivery' },
  { value: 'ordering', label: 'Ordering & Pricing' },
  { value: 'animal_care', label: 'Animal Care' },
  { value: 'farmhand', label: 'Farmhand App Help' },
  { value: 'policies', label: 'Policies & Procedures' },
  { value: 'products', label: 'Product Information' },
  { value: 'troubleshooting', label: 'Troubleshooting' },
  { value: 'general', label: 'General' },
];

const ArticlesList: React.FC = () => {
  const [articles, setArticles] = useState<KnowledgeArticle[]>([]);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [editingArticle, setEditingArticle] = useState<KnowledgeArticle | null>(null);

  useEffect(() => {
    fetchArticles();
  }, [categoryFilter]);

  const fetchArticles = async () => {
    try {
      let url = '/knowledge/articles/';
      if (categoryFilter) {
        url += `?category=${categoryFilter}`;
      }
      const res = await api.get(url);
      setArticles(res.data.results || res.data);
    } catch (error) {
      console.error('Error fetching articles:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!search.trim()) {
      fetchArticles();
      return;
    }
    setLoading(true);
    try {
      const res = await api.get(`/knowledge/articles/search/?q=${encodeURIComponent(search)}`);
      setArticles(res.data.results || res.data);
    } catch (error) {
      console.error('Error searching:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this article?')) return;
    try {
      await api.delete(`/knowledge/articles/${id}/`);
      fetchArticles();
    } catch (error) {
      console.error('Error deleting:', error);
    }
  };

  const handleSaveEdit = async () => {
    if (!editingArticle) return;
    try {
      await api.patch(`/knowledge/articles/${editingArticle.id}/`, {
        question: editingArticle.question,
        answer: editingArticle.answer,
        keywords: editingArticle.keywords,
      });
      setEditingArticle(null);
      fetchArticles();
    } catch (error) {
      console.error('Error saving:', error);
      alert('Error saving article');
    }
  };

  const filteredArticles = articles.filter((a) =>
    !search ||
    a.question.toLowerCase().includes(search.toLowerCase()) ||
    a.answer.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return <div className="knowledge-container"><div className="knowledge-loading">Loading...</div></div>;
  }

  return (
    <div className="knowledge-container">
      <div className="knowledge-header">
        <div className="header-left">
          <Link to="/knowledge" className="back-link">&larr; Knowledge Base</Link>
          <h1>Knowledge Articles</h1>
          <p className="knowledge-subtitle">{articles.length} articles in the knowledge base</p>
        </div>
        <Link to="/knowledge/articles/new" className="btn-action btn-green">
          + Add Article
        </Link>
      </div>

      {/* Filters */}
      <div className="filters-bar">
        <div className="search-group">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            placeholder="Search articles..."
            className="search-input"
          />
          <button onClick={handleSearch} className="btn-search">
            Search
          </button>
        </div>
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="category-select"
        >
          {categories.map(c => (
            <option key={c.value} value={c.value}>{c.label}</option>
          ))}
        </select>
      </div>

      {/* Articles List */}
      <div className="articles-list">
        {filteredArticles.length === 0 ? (
          <div className="no-articles">
            <p>No articles found</p>
            <Link to="/knowledge/articles/new" className="btn-action btn-green">
              Add your first article
            </Link>
          </div>
        ) : (
          filteredArticles.map((article) => (
            <div key={article.id} className="article-card">
              {editingArticle?.id === article.id ? (
                // Edit mode
                <div className="article-edit-form">
                  <input
                    type="text"
                    value={editingArticle.question}
                    onChange={(e) => setEditingArticle({ ...editingArticle, question: e.target.value })}
                    className="edit-question-input"
                    placeholder="Question"
                  />
                  <textarea
                    value={editingArticle.answer}
                    onChange={(e) => setEditingArticle({ ...editingArticle, answer: e.target.value })}
                    className="edit-answer-input"
                    placeholder="Answer"
                    rows={4}
                  />
                  <input
                    type="text"
                    value={editingArticle.keywords}
                    onChange={(e) => setEditingArticle({ ...editingArticle, keywords: e.target.value })}
                    className="edit-keywords-input"
                    placeholder="Keywords (comma-separated)"
                  />
                  <div className="edit-actions">
                    <button onClick={handleSaveEdit} className="btn-save">
                      Save
                    </button>
                    <button onClick={() => setEditingArticle(null)} className="btn-cancel">
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                // View mode
                <>
                  <div className="article-header">
                    <h3 className="article-question">{article.question}</h3>
                    <div className="article-badges">
                      <span className="badge badge-usage">{article.times_used} uses</span>
                      <span className="badge badge-category">
                        {article.category_display || categories.find(c => c.value === article.category)?.label || article.category}
                      </span>
                    </div>
                  </div>
                  <p className="article-answer">{article.answer}</p>
                  {article.keywords && (
                    <p className="article-keywords">
                      <strong>Keywords:</strong> {article.keywords}
                    </p>
                  )}
                  <div className="article-footer">
                    <span className="article-meta">
                      Created by {article.created_by_name || 'Unknown'} on {new Date(article.created_at).toLocaleDateString()}
                    </span>
                    <div className="article-actions">
                      <button
                        onClick={() => setEditingArticle(article)}
                        className="btn-edit"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(article.id)}
                        className="btn-delete"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ArticlesList;
